import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { templeInfo } from '../data/mock';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: 'Message Sent',
      description: 'Thank you for contacting us. We will respond within 24 hours.',
    });
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <div className="min-h-screen bg-[#ede1c5] text-stone-900">
      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/36481732/pexels-photo-36481732.jpeg)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#ede1c5]/85 via-[#ede1c5]/55 to-[#ede1c5]" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-amber-400/12 blur-[140px]" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 text-amber-700/90 tracking-[0.3em] text-[11px] uppercase mb-6">
            <span className="h-px w-12 bg-amber-600/60" />
            <Sparkles size={14} />
            <span>Reach out · We listen</span>
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] text-stone-900 max-w-3xl">
            We'd love to<br /><span className="italic text-amber-800">hear from you.</span>
          </h1>
        </div>
      </section>

      {/* CONTACT INFO */}
      <section className="py-20 border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-6">
          {[
            { icon: Phone, label: 'Phone', primary: templeInfo.phone, secondary: templeInfo.phone2, href: `tel:${templeInfo.phone}`, href2: `tel:${templeInfo.phone2}` },
            { icon: Mail, label: 'Email', primary: templeInfo.email, href: `mailto:${templeInfo.email}` },
            { icon: MapPin, label: 'Address', primary: templeInfo.address },
          ].map((b) => {
            const Icon = b.icon;
            return (
              <div
                key={b.label}
                className="p-8 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/80 hover:bg-[#fbf5e3]/95 hover:border-amber-500/40 transition-all text-center"
              >
                <div className="mx-auto mb-5 w-12 h-12 rounded-full bg-gradient-to-br from-amber-300 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <Icon className="text-black/80" size={20} />
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-700/80 mb-2">{b.label}</div>
                {b.href ? (
                  <a href={b.href} className="block text-stone-900 hover:text-amber-800 transition-colors">{b.primary}</a>
                ) : (
                  <div className="text-stone-900 leading-relaxed">{b.primary}</div>
                )}
                {b.secondary && (
                  <a href={b.href2} className="block text-stone-700 hover:text-amber-800 transition-colors mt-1">{b.secondary}</a>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* FORM + SIDE */}
      <section className="py-20 bg-[#e3d4af] border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              Send a Message
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900 mb-5 leading-tight">
              How can we help?
            </h2>
            <p className="text-stone-600 leading-relaxed mb-8">
              Questions about our programs, scheduling a visit, or seeking spiritual guidance — we're here.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5 p-8 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/80">
              {[
                { id: 'name', label: 'Full Name *', placeholder: 'John Doe', type: 'text', required: true },
                { id: 'email', label: 'Email Address *', placeholder: 'john@example.com', type: 'email', required: true },
                { id: 'phone', label: 'Phone Number', placeholder: '(123) 456-7890', type: 'tel' },
                { id: 'subject', label: 'Subject *', placeholder: 'How can we help you?', type: 'text', required: true },
              ].map((f) => (
                <div key={f.id}>
                  <label htmlFor={f.id} className="block text-[10px] tracking-[0.25em] uppercase text-amber-700/80 mb-2">
                    {f.label}
                  </label>
                  <Input
                    id={f.id}
                    name={f.id}
                    type={f.type}
                    required={f.required}
                    value={formData[f.id]}
                    onChange={handleChange}
                    placeholder={f.placeholder}
                    className="bg-[#f3e7c4]/60 border-stone-300 text-stone-900 focus:border-amber-300 placeholder:text-stone-600"
                  />
                </div>
              ))}
              <div>
                <label htmlFor="message" className="block text-[10px] tracking-[0.25em] uppercase text-amber-700/80 mb-2">
                  Message *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us more about your inquiry..."
                  className="bg-[#f3e7c4]/60 border-stone-300 text-stone-900 focus:border-amber-300 placeholder:text-stone-600 min-h-[140px]"
                />
              </div>
              <Button
                type="submit"
                data-testid="contact-submit-btn"
                className="w-full bg-amber-600 hover:bg-amber-600 text-black py-6 rounded-full font-medium tracking-wide"
              >
                Send Message <Send className="ml-2" size={16} />
              </Button>
            </form>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl overflow-hidden border border-amber-500/40">
              <img
                src="https://images.unsplash.com/photo-1558980971-97f50d0fed00"
                alt="Buddha statue"
                className="w-full h-80 object-cover"
              />
            </div>

            <div className="p-8 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/80">
              <h3 className="font-serif-display text-2xl text-stone-900 mb-5">Visit us</h3>
              <p className="text-stone-600 leading-relaxed mb-6">
                You are welcome to visit our temple any time during our program hours. No
                appointment necessary for regular sessions.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Meditation Classes', time: 'Wednesdays & Sundays · 6:00 PM' },
                  { title: 'Dharma Teachings', time: '' },
                  { title: "Children's Class", time: 'First Sunday · 2:00 PM – 4:00 PM' },
                ].map((s) => (
                  <div key={s.title} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-2.5" />
                    <div>
                      <div className="text-stone-900 font-medium">{s.title}</div>
                      {s.time && <div className="text-stone-600 text-sm">{s.time}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-8 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-[#fbf5e3]/95 to-[#f3e7c4]/90">
              <h3 className="font-serif-display text-2xl text-stone-900 mb-5">FAQ</h3>
              <div className="space-y-4">
                {[
                  { q: 'Do I need to be Buddhist to attend?', a: 'No! All are welcome regardless of background or beliefs.' },
                  { q: 'Is there a fee for programs?', a: 'All programs are free. Dāna (donations) are welcome but never required.' },
                  { q: 'What should I wear?', a: 'Comfortable, modest clothing is recommended. Remove shoes before entering meditation areas.' },
                  { q: 'Do I need meditation experience?', a: 'No prior experience needed — we guide beginners fully.' },
                ].map((f) => (
                  <div key={f.q}>
                    <div className="text-stone-900 font-medium mb-1">{f.q}</div>
                    <div className="text-stone-600 text-sm leading-relaxed">{f.a}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="py-20 border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">Find Us</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-stone-900 mb-2">
              Alabama Buddhist Vihāra
            </h2>
            <p className="text-stone-600">{templeInfo.address}</p>
          </div>

          <div
            className="relative rounded-3xl overflow-hidden border border-amber-500/40 shadow-2xl"
            style={{
              boxShadow: '0 30px 80px -30px rgba(120,80,20,0.35)',
            }}
          >
            {/* soft amber glow */}
            <div className="pointer-events-none absolute -inset-1 bg-gradient-to-br from-amber-300/20 via-transparent to-amber-500/20 rounded-3xl" />

            <iframe
              title="Alabama Buddhist Vihāra location"
              data-testid="contact-map-embed"
              src="https://maps.google.com/maps?q=2100%20Hardwick%20Road,%20Pell%20City,%20Alabama%2035128&t=&z=15&ie=UTF8&iwloc=&output=embed"
              className="relative w-full h-[500px] block"
              style={{ border: 0, filter: 'saturate(1.05) contrast(1.02)' }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>

          <div className="text-center mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
            <a
              href="https://maps.app.goo.gl/8HUobTgwTnJEsgbf6"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-open-google"
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-7 py-3.5 rounded-full text-sm tracking-wide shadow-md shadow-amber-700/20"
            >
              <MapPin size={15} /> Open in Google Maps
            </a>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=2100+Hardwick+Road,+Pell+City,+Alabama+35128"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-get-directions"
              className="inline-flex items-center gap-2 border border-amber-700/40 hover:border-amber-700 text-stone-800 hover:text-amber-800 hover:bg-amber-50 px-7 py-3.5 rounded-full text-sm tracking-wide"
            >
              Get Directions
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
